import toastTrigger from '@ui/toast/toast-trigger';
import getSessionLayoutFromSelectedBroadcastType from './get-session-layout-from-selected-broadcast-type';
import {
  getRequiredSessionDataIfFireside,
  getUpdatedProjectLanguagesWhenConvertingToFireside,
} from './get-required-session-data-if-fireside';
import {
  EBroadcastTypes,
  Session,
  SessionStreamingOptions,
  SessionTypesEnum,
  TPartialSessionWithUuid,
} from '@/types/session.types';
import { saveWorkingProjectByFields, saveWorkingProjectSessionByField } from '@/store/slices/project';
import getSessionTypeFromSelectedBroadcastType from './get-session-type-from-selected-broadcast-type';
import getSessionBroadcastTypeFromSelectedBroadcastType from './get-session-broadcast-type-from-selected-broadcast-type';
import { BLAdmin } from '@/types/profile.types';
import { TBroadcastTypePickerOptions } from '@/micros/shared-create-and-edit-components/grid-areas/main/project-overview/broadcast-type-picker/broadcast-type-picker.types';
import { useAppDispatch } from '@/store/hooks';
import { LanguagesAbbr } from '@/types/translation.types';
import { EventSettings } from '@/types/projects.types';
import { EToastTypes } from '@ui/toast/toast.types';

// Returns the updated streaming_options when broadcast-type change requires a
// write to streaming_options.rtmp_source, or null when no update is needed.
const getUpdatedStreamingOptions = (
  currentOptions: SessionStreamingOptions,
  newBroadcastType: EBroadcastTypes | null,
  selectedType: TBroadcastTypePickerOptions
): SessionStreamingOptions | null => {
  if (newBroadcastType !== EBroadcastTypes.rtmps) {
    if (currentOptions.rtmp_source === undefined) return null;
    const next = { ...currentOptions };
    delete next.rtmp_source;
    return next;
  }

  const rtmpSource = selectedType === 'teams' ? 'teams' : selectedType === 'zoom' ? 'zoom' : 'encoder';

  if (currentOptions.rtmp_source === rtmpSource) return null;

  return { ...currentOptions, rtmp_source: rtmpSource };
};

type TParams = {
  sessions?: Session[];
  session?: Session | null;
  user: BLAdmin | null;
  dispatch: ReturnType<typeof useAppDispatch>;
  type: TBroadcastTypePickerOptions;
  projectLanguages?: LanguagesAbbr[];
  projectDefaultLanguage?: string;
  workingProjectSettings?: EventSettings;
};

const handleBroadcastTypeChange = ({
  sessions,
  session,
  user,
  dispatch,
  type,
  projectLanguages,
  projectDefaultLanguage,
  workingProjectSettings,
}: TParams) => {
  if (!sessions || !session || !user) return;
  /*
      Note:
      The "BroadcastTypePicker" UI lists a hybrid of session_type and broadcast_type options.
      So we need to change both the broadcast type and session type based on the user's selection
    */

  const sessionType = getSessionTypeFromSelectedBroadcastType(type);
  const newBroadcastType = getSessionBroadcastTypeFromSelectedBroadcastType(type);
  const updatedStreamingOptions = getUpdatedStreamingOptions(session.streaming_options, newBroadcastType, type);

  // only need to pass in the fields you want to update in the api call
  const updatedSession: TPartialSessionWithUuid = {
    uuid: session.uuid,
    timestamp: session.timestamp,
    end_timestamp: session.end_timestamp,
    requires_time_change: session.requires_time_change,
    broadcast_type: newBroadcastType,
    session_type: sessionType,
    languages: session.languages,
    default_language: session.default_language,
    layout: {
      ...session.layout,
      ...getSessionLayoutFromSelectedBroadcastType(type),
    },
    ...(updatedStreamingOptions ? { streaming_options: updatedStreamingOptions } : {}),
  };

  const _updatedSession = getRequiredSessionDataIfFireside({
    session: updatedSession,
    user,
  });

  if ('error' in _updatedSession) {
    toastTrigger(_updatedSession.error, {
      type: EToastTypes.error,
    });
    return;
  }

  const isFireside = sessionType === SessionTypesEnum.fireside;

  const uniqueUpdatedProjectLanguages = getUpdatedProjectLanguagesWhenConvertingToFireside({
    projectLanguages,
    sessions,
    updatedSession: _updatedSession,
    projectDefaultLanguage,
    isFireside,
  });

  if (isFireside && workingProjectSettings) {
    dispatch(
      saveWorkingProjectByFields({
        settings: {
          ...workingProjectSettings,
          i18n: {
            ...(workingProjectSettings.i18n || { base: projectDefaultLanguage || 'en' }),
            event_languages: uniqueUpdatedProjectLanguages,
          },
        },
        saveId: 'broadcast-type-change',
        refetchProjectAfterSave: true,
        sessionsToSave: [_updatedSession],
      })
    );
  } else {
    dispatch(
      saveWorkingProjectSessionByField({
        ..._updatedSession,
        refetchProjectAfterSave: !!isFireside,
        saveId: 'broadcast-type-change',
      })
    );
  }
};

export default handleBroadcastTypeChange;
